import {
  changeEnrollmentFileRejectFilters,
  EnrollmentFileRejectFilters,
  getEnrollmentFileRejects,
  clearSort,
  changeSort,
  clearEnrollmentFileRejects,
} from 'Enrollment/ducks/enrollmentFileProcessingReject';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {isLoaded} from 'Core/models/Async';
import React, {useEffect} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {mapStateToProps} from 'Shared/components/ModalConductor';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import useReactRouter from 'use-react-router';
import PagedView from 'Shared/components/PagedView/PagedView';
import Column from 'Shared/components/PagedView/Column';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
  withInequalityFilterProperties,
} from 'Shared/components/PagedView/Filter';
import {FilterEnum} from 'Enrollment/ducks/enrollmentFileProcessingReject';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {MenuItem} from '@material-ui/core';
import {ModalState, showModal} from 'Core/ducks/modal';
import './EnrollmentFileRejects.sass';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {FilterOptionValue} from 'Core/models/FilterOption';
import {useLocation} from 'react-router-dom';
import qs from 'query-string';
import * as O from 'fp-ts/lib/Option';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIconWithCustomError} from 'Shared/components/Toolbar/ToolbarHeader';

const emailStatuses: FilterEnum[] = [
  {
    name: 'Sent',
    value: 'Sent',
  },
  {
    name: 'Unsent',
    value: 'Unsent',
  },
  {
    name: 'Deleted',
    value: 'Deleted',
  },
];

const applicationSources: FilterEnum[] = [
  {
    name: 'BP',
    value: 'ESR'
  },
  {
    name: 'Genelco',
    value: 'GenelcoSpreadsheet'
  }
];

const EnrollmentFileRejects = () => {
  const dispatch = useDispatch();
  const {history} = useReactRouter();
  const location = useLocation();
  const scopes = useSelector(selectUserScopes);

  const {enrollmentFileRejects, enrollmentFilesRejectFilter, sort} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessingReject
  );

  const queryStringCaseRequestID = qs.parse(location.search).caseRequestID as string | undefined;
  const queryStringEmailStatus = qs.parse(location.search).emailStatus as string | undefined;

  const filter: EnrollmentFileRejectFilters = enrollmentFilesRejectFilter;

  useEffect(() => {
    if (queryStringCaseRequestID) {
      filter.caseRequestID = queryStringCaseRequestID.toString();
    }

    if (queryStringEmailStatus) {
      filter.emailStatus = emailStatuses
        .filter(x => x.name === queryStringEmailStatus.toString())
        .map(FilterOptionValue.SelectedOption);

      changeEnrollmentFileRejectFilters(filter);
      history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_ERRORS);
    }
  }, [queryStringCaseRequestID, queryStringEmailStatus, filter.caseRequestID, filter.emailStatus, filter, history]);

  const changeFilter = withValueChangedHook(filter, changeEnrollmentFileRejectFilters, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: filter,
  };

  useEffect(() => {
    dispatch(getEnrollmentFileRejects(undefined, filter, O.toUndefined(sort)));
  }, [dispatch, filter, sort]);

  useEffect(() => {
    return () => {
      dispatch(clearEnrollmentFileRejects());
      dispatch(clearSort());
    };
  }, [clearEnrollmentFileRejects, changeEnrollmentFileRejectFilters]);

  return (
    <ModularHost>
      <ToolbarHeader title="Enrollment File Errors">
        <ExportToolbarHeaderIconWithCustomError
              title="Export Enrollment File Errors"
              downloadedFileName="Enrollment File Errors"
              extension={FileExtension.CSV}
              relativeFileLocation={`/enrollmentFiles/processingHistory/rejects/csv/${addFilterParameters({
                ...enrollmentFilesRejectFilter,
                exportToCsv: true,
              })}`}
              totalRecordCount={isLoaded(enrollmentFileRejects) ? enrollmentFileRejects.Data.totalCount : 0}
            />
      </ToolbarHeader>
      <PagedView
        pagedViewKey="enrollmentFileRejects"
        sort={O.toUndefined(sort)}
        onChangeSort={sort => dispatch(changeSort(sort))}
        data={enrollmentFileRejects}
        loadMore={page => dispatch(getEnrollmentFileRejects(page, filter))}
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="Case Request ID"
              sortColumnKey="caseRequestID"
              filterProperties={withStringFilterProperties(filterProperties, 'caseRequestID')}
            >
              {enrollmentFileReject => enrollmentFileReject.caseRequestID}
            </Column>
            <Column
              {...columnProps}
              title="File Name"
              sortColumnKey="fileName"
              filterProperties={withStringFilterProperties(filterProperties, 'fileName')}
              default
            >
              {enrollmentFileReject => enrollmentFileReject.fileName}
            </Column>
            <Column
              {...columnProps}
              title="Account Number"
              sortColumnKey="accountNumber"
              filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
              default
            >
              {enrollmentFileReject => enrollmentFileReject.accountNumber}
            </Column>
            <Column
              {...columnProps}
              title="System"
              filterProperties={withSelectFilterProperties(filterProperties, 'applicationSource', applicationSources, true)}
              default
            >
              {enrollmentFileReject => enrollmentFileReject.applicationSource === "ESR" ? 'BP' : 'Genelco'}
            </Column>
            <Column
              {...columnProps}
              title="Tech Partner"
              sortColumnKey="technologyPartner"
              filterProperties={withStringFilterProperties(filterProperties, 'technologyPartner')}
            >
              {enrollmentFileReject => enrollmentFileReject.technologyPartner}
            </Column>
            <Column
              {...columnProps}
              title="Evaluation Date"
              sortColumnKey="evaluationDate"
              filterProperties={withDateFilterProperties(filterProperties, 'evaluationDate')}
              default
            >
              {enrollmentFileReject => toFormattedDate(enrollmentFileReject.evaluationDate!)}
            </Column>
            <Column
              {...columnProps}
              title="Sent"
              sortColumnKey="mostRecentSentDate"
              filterProperties={withDateFilterProperties(filterProperties, 'mostRecentSentDate')}
            >
              {enrollmentFileReject =>
                enrollmentFileReject.mostRecentSentDate !== null
                  ? toFormattedDate(enrollmentFileReject.mostRecentSentDate!)
                  : 'N/A'
              }
            </Column>
            <Column
              {...columnProps}
              title="Deleted"
              sortColumnKey="deleteDate"
              filterProperties={withDateFilterProperties(filterProperties, 'deleteDate')}
            >
              {enrollmentFileReject =>
                enrollmentFileReject.deleteDate !== null ? toFormattedDate(enrollmentFileReject.deleteDate!) : 'N/A'
              }
            </Column>
            <Column
              {...columnProps}
              title="Total Apps"
              sortColumnKey="totalApps"
              filterProperties={withInequalityFilterProperties(filterProperties, 'totalApps')}
            >
              {enrollmentFileReject => enrollmentFileReject.totalApps}
            </Column>
            <Column
              {...columnProps}
              title="Unsuccessful Records"
              sortColumnKey="enrollTrackRejectApps"
              filterProperties={withInequalityFilterProperties(filterProperties, 'enrollTrackRejectApps')}
              default
            >
              {enrollmentFileReject => enrollmentFileReject.enrollTrackRejectApps}
            </Column>
            <Column
              {...columnProps}
              title="Action By"
              default
              sortColumnKey="rejectedBy"
              filterProperties={withStringFilterProperties(filterProperties, 'rejectedBy')}
            >
              {rejectFile => (rejectFile.rejectedBy ? rejectFile.rejectedBy.replace('AD\\', '') : 'N/A')}
            </Column>
            <Column
              {...columnProps}
              title="Email Status"
              filterProperties={withSelectFilterProperties(filterProperties, 'emailStatus', emailStatuses, true)}
              default
            >
              {enrollmentFileReject =>
                enrollmentFileReject.isEnrollmentFileRejectDeleted ? (
                  <span className="enrollment-file-rejects-label__deleted">Deleted</span>
                ) : enrollmentFileReject.isEmailSent ? (
                  <span className="enrollment-file-rejects-label__sent">Sent</span>
                ) : (
                  <span className="enrollment-file-rejects-label__unsent">Unsent</span>
                )
              }
            </Column>
            <Column {...columnProps} title="Actions" isActions default>
              {enrollmentFileReject => (
                <>
                  <ActionsMenu>
                    <MenuItem
                      onClick={() =>
                        history.push(
                          EnrollmentRouteConstants.ENROLLMENT_FILE_ERROR_DETAILS(
                            enrollmentFileReject.applicationSource === "ESR" ? enrollmentFileReject.caseRequestID!.toString() :
                            enrollmentFileReject.fileId!, enrollmentFileReject.applicationSource!
                          )
                        )
                      }
                    >
                      Details
                    </MenuItem>
                    <RequireScope scopes={scopes} scope={Scope.EnrollmentFileProcessingRejectRemove}>
                      <MenuItem
                        disabled={enrollmentFileReject.isEnrollmentFileRejectDeleted}
                        onClick={() =>
                          dispatch(
                            showModal(
                              ModalState.EnrollmentFileErrorDeleteConfirmation({
                                caseRequestId: enrollmentFileReject.caseRequestID!.toString(),
                              })
                            )
                          )
                        }
                      >
                        Delete
                      </MenuItem>
                    </RequireScope>
                  </ActionsMenu>
                </>
              )}
            </Column>
          </>
        )}
      </PagedView>
    </ModularHost>
  );
};

export default connect(mapStateToProps)(EnrollmentFileRejects);
